import { ClassifierOption } from '@dealstack/frontend/app/pages/Valuations/shared/entityClassiferBadgeConfig';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	GroupPersonSummary,
	SecurityBreakdown,
	ValuationSummaryGroupByMipRes,
} from '@dealstack/shared/lib/generated';
import { chain, forEach, isEmpty, map, mapValues, reduce } from 'lodash';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export type GlobalRowMetadata = {
	type?: string;
	depth?: number;
	isTotal?: boolean;
	isSummingRow?: boolean;
	classifiers?: ClassifierOption[];
	securitiesBreakdown?: SecurityBreakdown[];
};

export type NewGlobalValuationRow = {
	entity?: string;
	total?: HighPrecisionDecimal;
	subRows?: NewGlobalValuationRow[];
	metadata?: GlobalRowMetadata;
	[key: string]:
		| string
		| number
		| undefined
		| HighPrecisionDecimal
		| NewGlobalValuationRow[]
		| GlobalRowMetadata;
};

type PersonWithGroup = {
	id: string;
	displayName: string;
	classifiers?: string[];
	group: {
		id?: string;
		name?: string;
		generation: number;
	};
};

export function toGlobalTableData(
	summaryData: ValuationSummaryGroupByMipRes,
	tValuations: TranslateFunction<I18Namespaces.VALUATIONS>
): NewGlobalValuationRow[] {
	if (!summaryData) {
		return [];
	}

	// Create flattened array of persons with their group data
	const personsWithGroup: PersonWithGroup[] = chain(summaryData.groups)
		.flatMap((group) => map(group.persons, (person) => ({ ...person, group })))
		.value();

	const rows = chain(summaryData.groups)
		.flatMap((group) => {
			return chain(group.persons)
				.filter((person) => !isEmpty(person.outflows.securitiesBreakdown))
				.map((person) => {
					const entityTotal = new HighPrecisionDecimal(person.outflows.securities ?? '0');

					// First create a structure to hold both numeric totals and formatted strings
					const outflowData = {
						byPerson: {} as Record<string, HighPrecisionDecimal>,
						byGroup: {} as Record<string, HighPrecisionDecimal>,
					};

					forEach(person.outflows.securitiesBreakdown, (security) => {
						const personId = security.holderId;
						const mipId = security.holderGroupId;
						const columnId = mipId ? `${mipId}.${personId}` : personId;
						const amount = security.amount;

						// Add to person total
						outflowData.byPerson[columnId] = (
							outflowData.byPerson[columnId] ?? new HighPrecisionDecimal(0)
						).add(amount);

						// Add to group total if applicable
						if (mipId) {
							outflowData.byGroup[mipId] = (
								outflowData.byGroup[mipId] ?? new HighPrecisionDecimal(0)
							).add(amount);
						}
					});

					const outflowTotalsByPerson = {
						...outflowData.byPerson,
						...outflowData.byGroup,
					};

					// Create the column values for this row
					const personValues = {
						entity: person.displayName,
						total: entityTotal,
						...outflowTotalsByPerson,
					} satisfies NewGlobalValuationRow;

					return {
						...personValues,
						subRows: createSecuritySubRows(person, personsWithGroup, tValuations),
						metadata: {
							type: 'person',
							classifiers: (person.classifiers ?? []).map((tag) => ({
								label: tag,
								value: tag,
							})),
							securitiesBreakdown: person.outflows.securitiesBreakdown,
						},
					} satisfies NewGlobalValuationRow;
				})
				.value();
		})
		.value();

	const totalRows = createTotalRows(summaryData, tValuations);

	return [...rows, ...totalRows];
}

function createTotalRows(
	summaryData: ValuationSummaryGroupByMipRes,
	tValuations: TranslateFunction<I18Namespaces.VALUATIONS>
): NewGlobalValuationRow[] {
	// Define total row configurations with their data sources
	const totalRowConfigs = {
		securitiesHeld: {
			label: tValuations('summary.global.securities_held_row'),
			accessor: (person: GroupPersonSummary) =>
				new HighPrecisionDecimal(person.proceeds.securities ?? 0),
			isSummingRow: false,
		},
		receivables: {
			label: tValuations('summary.global.receivables_row'),
			accessor: (person: GroupPersonSummary) =>
				new HighPrecisionDecimal(person.proceeds.receivables ?? 0),
			isSummingRow: true,
		},
		cash: {
			label: tValuations('summary.global.cash_row'),
			accessor: (person: GroupPersonSummary) =>
				new HighPrecisionDecimal(person.proceeds.cash ?? 0),
			isSummingRow: true,
		},
		distributable: {
			label: tValuations('summary.global.distributable_row'),
			accessor: (person: GroupPersonSummary) =>
				new HighPrecisionDecimal(person.proceeds.total ?? 0),
			isSummingRow: false,
		},
		payables: {
			label: tValuations('summary.global.payables_row'),
			accessor: (person: GroupPersonSummary) =>
				new HighPrecisionDecimal(person.outflows.payables ?? 0).negated(),
			isSummingRow: true,
		},
		retained: {
			label: tValuations('summary.global.retained_row'),
			accessor: (person: GroupPersonSummary) =>
				new HighPrecisionDecimal(person.retained.total ?? 0).negated(),
			isSummingRow: true,
		},
		distributed: {
			label: tValuations('summary.global.distributed_row'),
			accessor: (person: GroupPersonSummary) =>
				new HighPrecisionDecimal(person.outflows.securities ?? 0),
			isSummingRow: false,
		},
	};

	// Initialize total row values structures
	const keyToSums: Record<keyof typeof totalRowConfigs, HighPrecisionDecimal> = mapValues(
		totalRowConfigs,
		() => new HighPrecisionDecimal(0)
	);
	const totalRowsData: Record<
		string,
		{
			entity: string;
			total: HighPrecisionDecimal;
			[key: string]: string | number | HighPrecisionDecimal;
		}
	> = mapValues(totalRowConfigs, (config) => ({
		entity: config.label,
		total: new HighPrecisionDecimal(0),
	}));

	// Process all groups at once
	forEach(summaryData.groups, (group) => {
		const groupTotals = mapValues(totalRowConfigs, () => new HighPrecisionDecimal(0));
		if (group.generation === 0) {
			// Skip the top level group
			return;
		}

		// Process each person in the group - calculate all totals
		forEach(group.persons, (person) => {
			const columnId = group.id ? `${group.id}.${person.id}` : person.id;

			// Process each metric in a single loop
			forEach(totalRowConfigs, (config, key) => {
				const value = config.accessor(person);

				groupTotals[key] = groupTotals[key].add(value);
				keyToSums[key] = keyToSums[key].add(value);

				totalRowsData[key][columnId] = value;
			});
		});

		// Add group totals to each row
		const mipId = group.id;
		if (mipId) {
			forEach(groupTotals, (value, key) => {
				totalRowsData[key][mipId] = value;
			});
		}
	});

	// Update the sums of some total rows
	forEach(totalRowConfigs, (config, key) => {
		if (config.isSummingRow) {
			totalRowsData[key].total = new HighPrecisionDecimal(keyToSums[key]);
		}
	});

	// Return array of row objects with appropriate metadata
	return [
		{
			...totalRowsData.securitiesHeld,
			metadata: { isTotal: true, type: 'total', isSummingRow: false },
		},
		{
			...totalRowsData.receivables,
			metadata: { type: 'total', isSummingRow: true },
		},
		{
			...totalRowsData.cash,
			metadata: { type: 'total', isSummingRow: true },
		},
		{
			...totalRowsData.distributable,
			metadata: { isTotal: true, type: 'total', isSummingRow: false },
		},
		{
			...totalRowsData.payables,
			metadata: { type: 'total', isSummingRow: true },
		},
		{
			...totalRowsData.retained,
			metadata: { type: 'total', isSummingRow: true },
		},
		{
			...totalRowsData.distributed,
			metadata: { isTotal: true, type: 'total', isSummingRow: false },
		},
	];
}

function createSecuritySubRows(
	person: GroupPersonSummary,
	columnPersons: PersonWithGroup[],
	tValuations: TranslateFunction<I18Namespaces.VALUATIONS>
): NewGlobalValuationRow[] {
	const securitiesBreakdown = person.outflows.securitiesBreakdown ?? [];
	if (isEmpty(securitiesBreakdown)) {
		return [];
	}

	// Group securities by type first
	const securitiesByType = chain(securitiesBreakdown).groupBy('securityType').value();

	// Process all security types and create rows
	return chain(Object.keys(securitiesByType))
		.sortBy((type) => type.toLowerCase())
		.map((securityType: string) => {
			const securitiesOfType = securitiesByType[securityType];

			// Group securities by ID for this security type
			const holdingsBySecurityId = chain(securitiesOfType).groupBy('id').value();
			const securityValuePerColumn: Record<string, HighPrecisionDecimal> = {
				total: new HighPrecisionDecimal(0),
			};

			// Create rows for each security ID
			const securityRows = chain(holdingsBySecurityId)
				.map((holdings) => {
					// Create a more efficient lookup of amounts by column ID
					// Only include holders that are in our columnPersons list
					const securityAmountByColumnId = chain(holdings)
						.keyBy((security) =>
							security.holderGroupId
								? `${security.holderGroupId}.${security.holderId}`
								: security.holderId
						)
						.mapValues('amount')
						.value();

					const securityTotal = reduce(
						holdings,
						(acc, security) => acc.add(security.amount ?? 0),
						new HighPrecisionDecimal(0)
					);
					securityValuePerColumn.total = securityValuePerColumn.total.add(securityTotal);

					// Initialize values object with entity and total
					let securitySubRowValues: Record<string, string | HighPrecisionDecimal> = {
						entity: holdings[0].name,
						total: securityTotal,
					};

					// Process all person columns in a single pass
					// Group totals will be accumulated as we go
					const groupTotals: Record<string, HighPrecisionDecimal> = {};

					// Calculate values for each column person
					forEach(columnPersons, (columnPerson) => {
						const personId = columnPerson.id;
						const mipId = columnPerson.group.id;
						const columnId = mipId ? `${mipId}.${personId}` : personId;

						const amount = new HighPrecisionDecimal(
							securityAmountByColumnId[columnId] ?? '0'
						);

						securitySubRowValues[columnId] = amount;

						// Update security row column totals
						securityValuePerColumn[columnId] = (
							securityValuePerColumn[columnId] ?? new HighPrecisionDecimal(0)
						).add(amount);

						// Update group column totals if applicable
						if (mipId) {
							groupTotals[mipId] = (
								groupTotals[mipId] ?? new HighPrecisionDecimal(0)
							).add(amount);
							securityValuePerColumn[mipId] = (
								securityValuePerColumn[mipId] ?? new HighPrecisionDecimal(0)
							).add(amount);
						}
					});

					// Add group totals to column values
					securitySubRowValues = {
						...securitySubRowValues,
						...groupTotals,
					};

					return {
						...securitySubRowValues,
						metadata: {
							type: 'security',
							depth: 2,
							securitiesBreakdown: holdings,
						},
					};
				})
				.value();

			// Create the security type row with totals
			const securityTypeRow: Record<string, string | number> = {
				entity: tValuations(`security_types.${securityType}`),
				...securityValuePerColumn,
			};

			return {
				...securityTypeRow,
				subRows: securityRows,
				metadata: {
					type: 'securityType',
					depth: 1,
				},
			};
		})
		.value();
}
